import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { DailySeries } from '@/api/worldCup';
import { AXIS_TICK, formatAxisTick } from '../ui/chartFormat';

/*
 * Date-axis variant of WeeklyBars. Used by France's consents-per-day chart,
 * which buckets by calendar day rather than ISO week. Same visual language as
 * WeeklyBars (mono font, faint axis, var(--ink) bars by default) so swapping
 * one for the other doesn't change the dashboard's feel.
 *
 * Tick density: with ~57 days in the campaign window, labeling every tick
 * would be illegible. We thin the labels to roughly one per week (every 7th
 * bar) and let Recharts keep the bars themselves.
 */
type Props = {
  series: DailySeries;
  color?: string;
  height?: number;
};

function formatDayLabel(iso: string): string {
  // 2026-05-25 → 25/05 — compact day/month, locale-agnostic.
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
}

export function DailyBars({ series, color = 'var(--ink)', height = 180 }: Props) {
  const data = series.series.map((p, i) => ({ name: formatDayLabel(p.date), value: p.value, index: i }));
  // Show ~8 tick labels total (matches the weekly cadence visually even though
  // bars are daily). Recharts' `interval` accepts a numeric step.
  const labelStep = Math.max(1, Math.floor(data.length / 8));

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
        {/* Horizontal-only gridlines give the bars a readable scale without
            boxing the chart in. */}
        <CartesianGrid vertical={false} stroke="var(--line)" strokeOpacity={0.6} />
        <XAxis dataKey="name" tickLine={false} axisLine={{ stroke: 'var(--line)' }} tick={AXIS_TICK} interval={labelStep - 1} />
        <YAxis tickLine={false} axisLine={false} width={40} allowDecimals={false} tick={AXIS_TICK} tickFormatter={formatAxisTick} />
        <Tooltip
          cursor={{ fill: 'var(--paper-2)' }}
          contentStyle={{ background: 'var(--paper)', border: '1.5px solid var(--line)', borderRadius: 6, fontSize: 12 }}
          labelStyle={{ color: 'var(--ink-soft)' }}
        />
        <Bar dataKey="value" fill={color} radius={[2, 2, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}
